import React, { useEffect, useState } from "react";
import axios from "axios";
import { toggleEnvironment } from '../../../utils/apiClient';
import { getAuthHeaders } from '../../../utils/api';
import imageCompression from 'browser-image-compression';
import TopBar from '../../../components/ui/TopBar/TopBar';

// TypeScript interfaces
interface Class {
  _id: string;
  name: string;
}

interface Student {
  _id: string;
  name: string;
  email: string;
}

interface Question {
  _id: string;
  question: string;
  marks: number;
  lowMarkRange: string;
  mediumMarkRange?: string;
  highMarkRange: string;
  rubric?: string;
}

const UploadStudentWork: React.FC = () => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedClass, setSelectedClass] = useState<string>("");
  const [selectedStudent, setSelectedStudent] = useState<string>("");
  const [selectedQuestion, setSelectedQuestion] = useState<string>("");
  const [images, setImages] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [extractedText, setExtractedText] = useState<string>("");
  const [isExtracting, setIsExtracting] = useState<boolean>(false);
  
  // Advanced image handling state
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [isConfirmingImages, setIsConfirmingImages] = useState<boolean>(false);
  const [pendingImages, setPendingImages] = useState<File[]>([]);
  const [pendingPreviews, setPendingPreviews] = useState<string[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [zoomImg, setZoomImg] = useState<string | null>(null);
  
  // Dropdown state for rubric details
  const [openDropdown, setOpenDropdown] = useState<'low' | 'medium' | 'high' | null>(null);
  
  // Class questions carousel state
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);

  // Fetch classes on component mount
  useEffect(() => {
    fetchClasses();
  }, []);

  // Fetch students when class is selected
  useEffect(() => {
    if (selectedClass) {
      fetchStudents(selectedClass);
    } else {
      setStudents([]);
      setSelectedStudent("");
    }
  }, [selectedClass]);

  // Fetch questions when class is selected
  useEffect(() => {
    if (selectedClass) {
      fetchQuestions(selectedClass);
    } else {
      setQuestions([]);
      setSelectedQuestion("");
    }
  }, [selectedClass]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (target && !target.closest('[data-dropdown-container]')) {
        setOpenDropdown(null);
      }
    };

    if (openDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  // Parse rubric text to extract sections
  const parseRubric = (rubricText: string) => {
    if (!rubricText) return { lowRange: '', mediumRange: '', highRange: '' };
    
    const lines = rubricText.split('\n');
    let lowRangeSection = '';
    let mediumRangeSection = '';
    let highRangeSection = '';
    let currentSection = '';
    
    for (const line of lines) {
      if (line.includes('Low Mark Range')) {
        currentSection = 'low';
        continue;
      } else if (line.includes('Medium Mark Range')) {
        currentSection = 'medium';
        continue;
      } else if (line.includes('High Mark Range')) {
        currentSection = 'high';
        continue;
      }
      
      if (currentSection === 'low' && line.trim()) {
        lowRangeSection += line.trim() + '\n';
      } else if (currentSection === 'medium' && line.trim()) {
        mediumRangeSection += line.trim() + '\n';
      } else if (currentSection === 'high' && line.trim()) {
        highRangeSection += line.trim() + '\n';
      }
    }
    
    return {
      lowRange: lowRangeSection.trim(),
      mediumRange: mediumRangeSection.trim(),
      highRange: highRangeSection.trim()
    };
  };

  // Get parsed rubric for selected question
  const getRubricSections = () => {
    const questionData = questions.find(q => q._id === selectedQuestion);
    if (!questionData || !questionData.rubric) {
      return { lowRange: '', mediumRange: '', highRange: '' };
    }
    return parseRubric(questionData.rubric);
  };

  const rubricSections = getRubricSections();

  // Carousel navigation functions
  const nextQuestion = (): void => {
    if (questions.length > 0) {
      setCurrentQuestionIndex((prev) => (prev + 1) % questions.length);
    }
  };

  const prevQuestion = (): void => {
    if (questions.length > 0) {
      setCurrentQuestionIndex((prev) => (prev - 1 + questions.length) % questions.length);
    }
  };

  const goToQuestion = (index: number): void => {
    setCurrentQuestionIndex(index);
  };

  // Get current question for carousel
  const getCurrentQuestion = () => {
    if (questions.length === 0) return null;
    return questions[currentQuestionIndex];
  };

  // Reset carousel when questions change
  useEffect(() => {
    setCurrentQuestionIndex(0);
  }, [questions]);

  const fetchClasses = async (): Promise<void> => {
    try {
      const response = await axios.get('/api/v1/stem@home/teacher/classes', {
        baseURL: toggleEnvironment(),
        headers: getAuthHeaders()
      });
      setClasses(response.data.data || []);
    } catch (error) {
      console.error('Error fetching classes:', error);
      setError('Failed to fetch classes');
    }
  };

  const fetchStudents = async (classId: string): Promise<void> => {
    try {
      const response = await axios.get(`/api/v1/stem@home/teacher/class/${classId}/students`, {
        baseURL: toggleEnvironment(),
        headers: getAuthHeaders()
      });
      // The students array is nested under data.students and uses 'id' instead of '_id'
      const studentsList = response.data.data.students || [];
      console.log('Students response:', response.data); // For debugging
      setStudents(studentsList.map((student: any) => ({
        _id: student.id, // Map id to _id to match the expected format
        name: student.name,
        email: student.email
      })));
      setError(''); // Clear any existing errors
    } catch (error) {
      console.error('Error fetching students:', error);
      setError('Failed to fetch students for this class');
      setStudents([]);
    }
  };

  const fetchQuestions = async (classId: string): Promise<void> => {
    try {
      const response = await axios.get(`/api/v1/stem@home/question/questions/class/${classId}`, {
        baseURL: toggleEnvironment(),
        headers: getAuthHeaders()
      });
      setQuestions(response.data.data || []);
      setError(''); // Clear any existing errors
    } catch (error) {
      console.error('Error fetching questions:', error);
      setError('Failed to fetch questions for this class');
      setQuestions([]);
    }
  };

  const handleClassChange = (value: string): void => {
    setSelectedClass(value);
    setSelectedStudent("");
    setSelectedQuestion("");
    setError("");
  };

  const handleStudentChange = (value: string): void => {
    setSelectedStudent(value);
  };

  const handleQuestionChange = (value: string): void => {
    setSelectedQuestion(value);
  };

  const compressImage = async (imageFile: File): Promise<File> => {
    const options = {
      maxSizeMB: 9.5,
      maxWidthOrHeight: 2048,
      useWebWorker: true,
      initialQuality: 0.8
    };
    
    try {
      const compressedFile = await imageCompression(imageFile, options);
      if (compressedFile.size > 9.5 * 1024 * 1024) {
        const secondOptions = {
          maxSizeMB: 5,
          maxWidthOrHeight: 2048,
          useWebWorker: true,
          initialQuality: 0.7
        };
        return await imageCompression(compressedFile, secondOptions);
      }
      return compressedFile;
    } catch (error) {
      console.error('Error compressing image:', error);
      throw error;
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const currentTotal = pendingImages.length + files.length;
    
    // Enforce maximum of 5 images
    if (currentTotal > 5) {
      setError(`You can upload a maximum of 5 images total. Currently have ${pendingImages.length} images.`);
      if (event.target) event.target.value = '';
      return;
    }

    // Validate file types
    const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/tiff', 'application/pdf'];
    const invalidFile = files.find(f => !supportedTypes.includes(f.type));
    if (invalidFile) {
      setError('Please upload only supported file types (JPEG/JPG, PNG, PDF, or TIFF)');
      if (event.target) event.target.value = '';
      return;
    }

    try {
      setError('');

      const newImages: File[] = [];
      const newPreviews: string[] = [];

      // Process images for preview only
      for (const file of files) {
        const compressedImage = await compressImage(file);
        newImages.push(compressedImage);

        // Create preview
        const previewReader = await new Promise<string>((resolve) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result as string);
          r.readAsDataURL(compressedImage);
        });
        newPreviews.push(previewReader);
      }

      // Add new images to pending arrays
      setPendingImages([...pendingImages, ...newImages]);
      setPendingPreviews([...pendingPreviews, ...newPreviews]);
      setIsConfirmingImages(true);
      
      // Clear the input
      if (event.target) event.target.value = '';
    } catch (err) {
      console.error('Error processing images', err);
      setError('Error processing images. Please try again.');
    }
  };

  const handleAddMoreImages = (): void => {
    // Trigger file input click
    const fileInput = document.getElementById('additional-images-input') as HTMLInputElement;
    fileInput?.click();
  };

  const handleRemoveImage = (index: number): void => {
    const updatedImages = pendingImages.filter((_, i) => i !== index);
    const updatedPreviews = pendingPreviews.filter((_, i) => i !== index);
    
    setPendingImages(updatedImages);
    setPendingPreviews(updatedPreviews);
    
    // If no images left, exit confirmation mode
    if (updatedImages.length === 0) {
      setIsConfirmingImages(false);
    }
  };

  const handleDragStart = (e: React.DragEvent, index: number): void => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent): void => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number): void => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const newImages = [...pendingImages];
    const newPreviews = [...pendingPreviews];
    
    // Remove dragged items
    const draggedImage = newImages.splice(draggedIndex, 1)[0];
    const draggedPreview = newPreviews.splice(draggedIndex, 1)[0];
    
    // Insert at new position
    newImages.splice(dropIndex, 0, draggedImage);
    newPreviews.splice(dropIndex, 0, draggedPreview);
    
    setPendingImages(newImages);
    setPendingPreviews(newPreviews);
    setDraggedIndex(null);
  };

  const handleConfirmImages = async (): Promise<void> => {
    if (pendingImages.length === 0) return;

    try {
      setIsExtracting(true);
      setError('');

      let combinedRawText = '';

      // Extract text from confirmed images in order
      for (const image of pendingImages) {
        const formData = new FormData();
        formData.append('file', image);

        const response = await axios.post('/api/textract/teacher/extract-text', formData, {
          baseURL: toggleEnvironment(),
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data',
          },
        });

        if (response.data.status === 'success') {
          const rawText = response.data.data.extractedText;
          if (rawText) {
            combinedRawText += (combinedRawText ? '\n\n' : '') + rawText;
          }
        }
      }

      // Try to cleanup text via Claude (optional)
      let cleanedText = combinedRawText;
      try {
        const claudeResponse = await axios.post(
          '/api/claude/cleanup-text',
          { text: combinedRawText },
          {
            baseURL: toggleEnvironment(),
            headers: getAuthHeaders()
          }
        );
        if (claudeResponse.data.status === 'success') {
          cleanedText = claudeResponse.data.data.cleanedText;
        }
      } catch (claudeErr) {
        console.error('Claude cleanup error:', claudeErr);
        // Continue with raw text if Claude cleanup fails
      }

      // Set final images and text
      setSelectedImages(pendingImages);
      setImages(pendingImages); // Keep for compatibility
      setPreviewUrls(pendingPreviews);
      setExtractedText(cleanedText);
      
      // Exit confirmation mode
      setIsConfirmingImages(false);
      setPendingImages([]);
      setPendingPreviews([]);
    } catch (err) {
      console.error('Error extracting text', err);
      setError('Error extracting text. Please try again.');
    } finally {
      setIsExtracting(false);
    }
  };

  const handleCancelImages = (): void => {
    setPendingImages([]);
    setPendingPreviews([]);
    setIsConfirmingImages(false);
    setError('');
  };

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();

    if (!selectedClass || !selectedStudent || !selectedQuestion) {
      setError('Please select a class, student, and question');
      return;
    }

    if (selectedImages.length === 0) {
      setError('Please upload at least one image');
      return;
    }

    if (!extractedText.trim()) {
      setError('Please provide an answer');
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit for marking using the correct endpoint and format
      const response = await axios.post('/api/textract/teacher/submit-for-student', {
        studentId: selectedStudent,
        classId: selectedClass,
        questionId: selectedQuestion,
        extractedText: extractedText.trim()
      }, {
        baseURL: toggleEnvironment(),
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        }
      });

      if (response.data.status === 'success') {
        // Reset form
        setSelectedClass("");
        setSelectedStudent("");
        setSelectedQuestion("");
        setImages([]);
        setSelectedImages([]);
        setPreviewUrls([]);
        setExtractedText("");
        setError("");
        
        alert('Student work submitted successfully!');
        // Optionally reload the page like the original
        // window.location.reload();
      } else {
        setError(response.data.message || 'Failed to submit student work');
      }
    } catch (error) {
      console.error('Error submitting student work:', error);
      setError('Failed to submit student work');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get selected question data or fallback to dashes for display
  const selectedQuestionData = questions.find(q => q._id === selectedQuestion) || {
    _id: '',
    question: 'Please select a question first',
    marks: 0,
    lowMarkRange: '---',
    mediumMarkRange: undefined,
    highMarkRange: '---',
    rubric: ''
  };
  
  // Debug logging for rubric data
  React.useEffect(() => {
    if (selectedQuestion && selectedQuestionData.rubric) {
      console.log('Selected question rubric data:', {
        questionId: selectedQuestion,
        rubric: selectedQuestionData.rubric,
        parsedSections: getRubricSections()
      });
    }
  }, [selectedQuestion, selectedQuestionData.rubric]);

  return (
    <>
      <TopBar 
        title="Upload Student Work"
        showActions={true} 
      />
      <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', padding: '24px', paddingTop: '120px' }}>

      {/* Main Content */}
      <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
        {/* Upload Student Work - Left Side */}
        <div style={{ flex: '2', minWidth: '500px' }}>
          <div style={{ 
            padding: '24px', 
            backgroundColor: 'white', 
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)', 
            border: '1px solid #e5e7eb', 
            borderRadius: '12px' 
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px' }}>
              <svg style={{ width: '20px', height: '20px', color: '#2563eb' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: 0 }}>Upload Student Work</h2>
            </div>
            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px' }}>Select a class and student, then upload their work for text extraction</p>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Selection Row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
                {/* Select Class */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <svg style={{ width: '16px', height: '16px', color: '#6b7280' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Select Class</label>
                  </div>
                  <select 
                    value={selectedClass} 
                    onChange={(e) => handleClassChange(e.target.value)}
                    style={{ 
                      width: '100%', 
                      height: '40px', 
                      padding: '0 12px', 
                      border: '1px solid #d1d5db', 
                      borderRadius: '8px', 
                      backgroundColor: 'white', 
                      fontSize: '14px',
                      color: selectedClass ? '#111827' : '#9ca3af'
                    }}
                  >
                    <option value="" disabled>Select a class</option>
                    {classes.map(c => (
                      <option key={c._id} value={c._id} style={{ color: '#111827' }}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Select Student */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <svg style={{ width: '16px', height: '16px', color: '#6b7280' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Select Student</label>
                  </div>
                  <select 
                    value={selectedStudent} 
                    onChange={(e) => handleStudentChange(e.target.value)}
                    disabled={!selectedClass}
                    style={{ 
                      width: '100%', 
                      height: '40px', 
                      padding: '0 12px', 
                      border: '1px solid #d1d5db', 
                      borderRadius: '8px', 
                      backgroundColor: selectedClass ? 'white' : '#f3f4f6', 
                      fontSize: '14px',
                      color: selectedStudent ? '#111827' : '#9ca3af',
                      cursor: selectedClass ? 'pointer' : 'not-allowed'
                    }}
                  >
                    <option value="" disabled>Select a student</option>
                    {students.map(student => (
                      <option key={student._id} value={student._id} style={{ color: '#111827' }}>
                        {student.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Select Question */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <svg style={{ width: '16px', height: '16px', color: '#6b7280' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Select Question</label>
                  </div>
                  <select 
                    value={selectedQuestion} 
                    onChange={(e) => handleQuestionChange(e.target.value)}
                    disabled={!selectedClass}
                    style={{ 
                      width: '100%', 
                      height: '40px', 
                      padding: '0 12px', 
                      border: '1px solid #d1d5db', 
                      borderRadius: '8px', 
                      backgroundColor: selectedClass ? 'white' : '#f3f4f6', 
                      fontSize: '14px',
                      color: selectedQuestion ? '#111827' : '#9ca3af',
                      cursor: selectedClass ? 'pointer' : 'not-allowed'
                    }}
                  >
                    <option value="" disabled>Select a question</option>
                    {questions.map(question => (
                      <option key={question._id} value={question._id} style={{ color: '#111827' }}>
                        {question.question}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Upload Work Section */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Upload Work (Max 5 images)</label>
                
                {!isConfirmingImages && selectedImages.length === 0 && (
                  <div style={{ 
                    border: '2px dashed #d1d5db', 
                    borderRadius: '8px', 
                    padding: '32px', 
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s'
                  }} 
                  onClick={() => document.getElementById('file-upload')?.click()}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                      <div style={{ 
                        width: '48px', 
                        height: '48px', 
                        backgroundColor: '#f3f4f6', 
                        borderRadius: '8px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center' 
                      }}>
                        <svg style={{ width: '24px', height: '24px', color: '#9ca3af' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                        <button
                          type="button"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px 24px',
                            backgroundColor: '#000000',
                            color: 'white',
                            borderRadius: '8px',
                            border: 'none',
                            cursor: (!selectedClass || !selectedStudent || !selectedQuestion) ? 'not-allowed' : 'pointer',
                            opacity: (!selectedClass || !selectedStudent || !selectedQuestion) ? 0.5 : 1,
                            transition: 'all 0.3s ease',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                          disabled={!selectedClass || !selectedStudent || !selectedQuestion}
                          onClick={(e) => {
                            e.stopPropagation();
                            document.getElementById('file-upload')?.click();
                          }}
                          onMouseEnter={(e) => {
                            if (selectedClass && selectedStudent && selectedQuestion) {
                              e.currentTarget.style.backgroundColor = '#333333';
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (selectedClass && selectedStudent && selectedQuestion) {
                              e.currentTarget.style.backgroundColor = '#000000';
                              e.currentTarget.style.transform = 'translateY(0px)';
                              e.currentTarget.style.boxShadow = 'none';
                            }
                          }}
                        >
                          <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          Upload Images
                        </button>
                        <p style={{ fontSize: '14px', color: '#6b7280', margin: '12px 0 0 0' }}>Select class, student and question first</p>
                        <p style={{ fontSize: '12px', color: '#9ca3af', margin: '4px 0 0 0' }}>Supports JPG, PNG (Max 5 files)</p>
                      </div>
                    </div>
                    <input
                      id="file-upload"
                      type="file"
                      multiple
                      accept="image/*,.pdf"
                      onChange={handleImageUpload}
                      disabled={!selectedClass || !selectedStudent || !selectedQuestion}
                      style={{ display: 'none' }}
                    />
                  </div>
                )}

                {isConfirmingImages && (
                  <div style={{ 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '12px', 
                    padding: '24px', 
                    backgroundColor: '#f9fafb' 
                  }}>
                    <div style={{ marginBottom: '16px' }}>
                      <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: '0 0 8px 0' }}>
                        Confirm Image Order ({pendingImages.length}/5)
                      </h4>
                      <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                        Drag and drop to reorder images. Click ✕ to remove an image.
                      </p>
                    </div>
                    
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                      gap: '16px', 
                      marginBottom: '24px',
                      overflow: 'visible'
                    }}>
                      {pendingPreviews.map((url, index) => (
                        <div
                          key={index}
                          style={{
                            position: 'relative',
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            border: draggedIndex === index ? '3px solid #3b82f6' : '2px solid #d1d5db',
                            cursor: 'grab',
                            transition: 'all 0.2s',
                            transform: draggedIndex === index ? 'scale(1.03)' : 'scale(1)',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            height: '180px',
                            overflow: 'visible',
                            padding: '8px'
                          }}
                          className="image-container-with-badges"
                          draggable
                          onDragStart={(e) => handleDragStart(e, index)}
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, index)}
                        >
                          <div 
                            style={{ 
                              position: 'relative', 
                              width: '100%', 
                              height: '100%',
                              isolation: 'isolate'
                            }}
                          >
                            <img 
                              src={url} 
                              alt={`Preview ${index + 1}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setZoomImg(url);
                              }}
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                objectFit: 'contain',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                backgroundColor: '#f8fafc',
                                display: 'block'
                              }}
                            />
                          </div>
                          
                          <style>
                            {`
                              .image-container-with-badges .number-badge-${index} {
                                position: absolute !important;
                                top: 12px !important;
                                left: 12px !important;
                                width: 28px !important;
                                height: 28px !important;
                                background-color: #3b82f6 !important;
                                color: white !important;
                                border-radius: 50% !important;
                                display: flex !important;
                                align-items: center !important;
                                justify-content: center !important;
                                font-size: 14px !important;
                                font-weight: bold !important;
                                z-index: 999999 !important;
                                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3) !important;
                                border: 2px solid white !important;
                                pointer-events: none !important;
                              }
                              .image-container-with-badges .x-button-${index} {
                                position: absolute !important;
                                top: 12px !important;
                                right: 12px !important;
                                width: 28px !important;
                                height: 28px !important;
                                background-color: #dc2626 !important;
                                color: white !important;
                                border: 2px solid white !important;
                                border-radius: 50% !important;
                                cursor: pointer !important;
                                display: flex !important;
                                align-items: center !important;
                                justify-content: center !important;
                                font-size: 16px !important;
                                font-weight: bold !important;
                                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3) !important;
                                z-index: 999999 !important;
                                transition: all 0.2s !important;
                              }
                              .image-container-with-badges .x-button-${index}:hover {
                                background-color: #b91c1c !important;
                                transform: scale(1.1) !important;
                              }
                            `}
                          </style>
                          
                          {/* Number Badge - Top Left */}
                          <div className={`number-badge-${index}`}>
                            {index + 1}
                          </div>
                          
                          {/* X Button - Top Right */}
                          <button
                            type="button"
                            className={`x-button-${index}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              console.log('Remove button clicked for index:', index);
                              handleRemoveImage(index);
                            }}
                            title="Remove image"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {pendingImages.length < 5 && (
                        <button
                          type="button"
                          onClick={handleAddMoreImages}
                          style={{
                            padding: '10px 20px',
                            backgroundColor: '#000000',
                            border: 'none',
                            borderRadius: '8px',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#333333';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#000000';
                            e.currentTarget.style.transform = 'translateY(0px)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          + Add More Images ({5 - pendingImages.length} remaining)
                        </button>
                      )}
                      
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <button
                          type="button"
                          onClick={handleCancelImages}
                          style={{
                            flex: 1,
                            padding: '10px 20px',
                            backgroundColor: 'white',
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            color: '#374151',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleConfirmImages}
                          disabled={pendingImages.length === 0}
                          style={{
                            flex: 1,
                            padding: '10px 20px',
                            backgroundColor: pendingImages.length === 0 ? '#9ca3af' : '#000000',
                            border: 'none',
                            borderRadius: '8px',
                            color: 'white',
                            cursor: pendingImages.length === 0 ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (pendingImages.length > 0) {
                              e.currentTarget.style.backgroundColor = '#333333';
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (pendingImages.length > 0) {
                              e.currentTarget.style.backgroundColor = '#000000';
                              e.currentTarget.style.transform = 'translateY(0px)';
                              e.currentTarget.style.boxShadow = 'none';
                            }
                          }}
                        >
                          Confirm & Extract Text
                        </button>
                      </div>

                      {/* Hidden input for adding more images */}
                      <input
                        id="additional-images-input"
                        type="file"
                        onChange={handleImageUpload}
                        accept="image/*,.pdf"
                        multiple
                        style={{ display: 'none' }}
                      />
                    </div>
                  </div>
                )}

                {previewUrls.length > 0 && !isConfirmingImages && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg style={{ width: '16px', height: '16px', color: '#059669' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                          Final Images ({previewUrls.length})
                        </label>
                      </div>
                      <button 
                        type="button" 
                        onClick={() => {
                          setSelectedImages([]);
                          setImages([]);
                          setPreviewUrls([]);
                          setExtractedText('');
                        }}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#f3f4f6',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          color: '#374151',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '500',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                      >
                        Replace Images
                      </button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', maxWidth: '500px' }}>
                      {previewUrls.map((url, index) => (
                        <div key={index} style={{ position: 'relative', overflow: 'visible' }}>
                          <div style={{
                            position: 'absolute !important' as any,
                            top: '8px !important' as any,
                            left: '8px !important' as any,
                            width: '28px !important' as any,
                            height: '28px !important' as any,
                            backgroundColor: '#059669 !important' as any,
                            color: 'white !important' as any,
                            borderRadius: '50% !important' as any,
                            display: 'flex !important' as any,
                            alignItems: 'center !important' as any,
                            justifyContent: 'center !important' as any,
                            fontSize: '14px !important' as any,
                            fontWeight: '700 !important' as any,
                            zIndex: '10000001 !important' as any,
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.4) !important' as any,
                            border: '3px solid white !important' as any,
                            pointerEvents: 'none !important' as any
                          }}>
                            {index + 1}
                          </div>
                          <img 
                            src={url} 
                            alt={`Final image ${index + 1}`}
                            onClick={() => setZoomImg(url)}
                            style={{ 
                              width: '100%', 
                              height: '140px', 
                              objectFit: 'contain',
                              borderRadius: '10px', 
                              border: '2px solid #d1d5db',
                              cursor: 'pointer',
                              backgroundColor: '#f8fafc',
                              padding: '6px',
                              transition: 'all 0.2s',
                              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'scale(1.02)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'scale(1)';
                              e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.1)';
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Extracted Text Section - Only show when there's text or currently extracting */}
              {(extractedText.trim() || isExtracting) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                    {isExtracting ? 'Extracting text...' : 'Extracted Text (Edit if needed)'}
                  </label>
                  <textarea
                    value={extractedText}
                    onChange={(e) => setExtractedText(e.target.value)}
                    placeholder="Extracted text will appear here..."
                    rows={6}
                    disabled={isExtracting}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      resize: 'none',
                      fontFamily: 'inherit',
                      backgroundColor: isExtracting ? '#f9fafb' : 'white'
                    }}
                  />
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div style={{ padding: '12px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px' }}>
                  <p style={{ fontSize: '14px', color: '#dc2626', margin: 0 }}>{error}</p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting || !extractedText.trim() || !selectedClass || !selectedStudent || !selectedQuestion || selectedImages.length === 0 || isConfirmingImages}
                style={{
                  width: '100%',
                  height: '48px',
                  backgroundColor: (isSubmitting || !extractedText.trim() || !selectedClass || !selectedStudent || !selectedQuestion || selectedImages.length === 0 || isConfirmingImages) ? '#9ca3af' : '#000000',
                  color: 'white',
                  fontWeight: '500',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: (isSubmitting || !extractedText.trim() || !selectedClass || !selectedStudent || !selectedQuestion || selectedImages.length === 0 || isConfirmingImages) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease',
                  fontSize: '14px'
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting && extractedText.trim() && selectedClass && selectedStudent && selectedQuestion && selectedImages.length > 0 && !isConfirmingImages) {
                    e.currentTarget.style.backgroundColor = '#333333';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting && extractedText.trim() && selectedClass && selectedStudent && selectedQuestion && selectedImages.length > 0 && !isConfirmingImages) {
                    e.currentTarget.style.backgroundColor = '#000000';
                    e.currentTarget.style.transform = 'translateY(0px)';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                {isSubmitting ? 'Uploading...' : 'Upload Work'}
              </button>
            </form>
          </div>
        </div>

        {/* Selected Question and Class Questions - Right Side */}
        <div style={{ flex: '1', minWidth: '300px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Selected Question Box */}
          <div style={{ 
            padding: '24px', 
            backgroundColor: 'white', 
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)', 
            border: '3px solid #f59e0b', 
            borderRadius: '12px' 
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px' }}>
              <svg style={{ width: '20px', height: '20px', color: '#f59e0b' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: 0 }}>Selected Question</h2>
            </div>
            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px' }}>Rubric details for the selected question</p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '8px', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280' }}>Question</span>
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280' }}>Marks</span>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <p style={{ fontSize: '14px', color: '#111827', margin: 0, paddingRight: '16px', flex: 1 }}>{selectedQuestionData.question}</p>
                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>{selectedQuestionData.marks}</span>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ position: 'relative' }} data-dropdown-container>
                    <button
                      type="button"
                      onClick={() => {
                        console.log('Low Range button clicked, current dropdown:', openDropdown);
                        setOpenDropdown(openDropdown === 'low' ? null : 'low');
                      }}
                      style={{ 
                        width: '100%',
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between', 
                        padding: '12px', 
                        backgroundColor: selectedQuestion ? '#fef2f2' : '#f9fafb', 
                        borderRadius: '8px', 
                        border: selectedQuestion ? '2px solid #fecaca' : '1px solid #e5e7eb',
                        cursor: selectedQuestion ? 'pointer' : 'not-allowed',
                        opacity: selectedQuestion ? 1 : 0.5,
                        transition: 'all 0.2s',
                        boxShadow: selectedQuestion ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none'
                      }}
                      disabled={!selectedQuestion}
                      onMouseEnter={(e) => {
                        if (selectedQuestion) {
                          e.currentTarget.style.backgroundColor = '#fde8e8';
                          e.currentTarget.style.borderColor = '#f87171';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedQuestion) {
                          e.currentTarget.style.backgroundColor = '#fef2f2';
                          e.currentTarget.style.borderColor = '#fecaca';
                        }
                      }}
                    >
                      <span style={{ fontSize: '14px', fontWeight: '600', color: selectedQuestion ? '#dc2626' : '#6b7280' }}>
                        Low Range {selectedQuestion ? '(Click to view rubric)' : ''}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ 
                          padding: '4px 8px', 
                          backgroundColor: selectedQuestion ? '#dc2626' : '#9ca3af', 
                          color: 'white', 
                          fontSize: '12px', 
                          fontWeight: '500', 
                          borderRadius: '4px' 
                        }}>
                          {selectedQuestionData.lowMarkRange}
                        </span>
                        {selectedQuestion && (
                          <svg 
                            style={{ 
                              width: '16px', 
                              height: '16px', 
                              color: '#dc2626',
                              transform: openDropdown === 'low' ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s'
                            }} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </div>
                    </button>

                    {openDropdown === 'low' && selectedQuestion && (
                      <div style={{
                        position: 'absolute !important' as any,
                        top: '100% !important' as any,
                        left: '0 !important' as any,
                        right: '0 !important' as any,
                        marginTop: '4px !important' as any,
                        backgroundColor: 'white !important' as any,
                        border: '2px solid #fecaca !important' as any,
                        borderRadius: '8px !important' as any,
                        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15) !important' as any,
                        zIndex: '10000002 !important' as any,
                        padding: '16px !important' as any,
                        maxHeight: '200px !important' as any,
                        overflowY: 'auto !important' as any
                      }}>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#dc2626', marginBottom: '8px' }}>
                          Low Range Rubric ({selectedQuestionData.lowMarkRange} marks)
                        </div>
                        <div style={{ fontSize: '13px', color: '#374151', lineHeight: '1.5', whiteSpace: 'pre-line' }}>
                          {rubricSections.lowRange || 'No rubric available for this question.'}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Medium Range - only show if the question has a medium range */}
                  {selectedQuestionData.mediumMarkRange && (
                    <div style={{ position: 'relative' }} data-dropdown-container>
                      <button
                        type="button"
                        onClick={() => {
                          console.log('Medium Range button clicked, current dropdown:', openDropdown);
                          setOpenDropdown(openDropdown === 'medium' ? null : 'medium');
                        }}
                        style={{ 
                          width: '100%',
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between', 
                          padding: '12px', 
                          backgroundColor: selectedQuestion ? '#fef3c7' : '#f9fafb', 
                          borderRadius: '8px', 
                          border: selectedQuestion ? '2px solid #fde68a' : '1px solid #e5e7eb',
                          cursor: selectedQuestion ? 'pointer' : 'not-allowed',
                          opacity: selectedQuestion ? 1 : 0.5,
                          transition: 'all 0.2s',
                          boxShadow: selectedQuestion ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none'
                        }}
                        disabled={!selectedQuestion}
                        onMouseEnter={(e) => {
                          if (selectedQuestion) {
                            e.currentTarget.style.backgroundColor = '#fef3c7';
                            e.currentTarget.style.borderColor = '#fbbf24';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedQuestion) {
                            e.currentTarget.style.backgroundColor = '#fef3c7';
                            e.currentTarget.style.borderColor = '#fde68a';
                          }
                        }}
                      >
                        <span style={{ fontSize: '14px', fontWeight: '600', color: selectedQuestion ? '#d97706' : '#6b7280' }}>
                          Medium Range {selectedQuestion ? '(Click to view rubric)' : ''}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ 
                            padding: '4px 8px', 
                            backgroundColor: selectedQuestion ? '#d97706' : '#9ca3af', 
                            color: 'white', 
                            fontSize: '12px', 
                            fontWeight: '500', 
                            borderRadius: '4px' 
                          }}>
                            {selectedQuestionData.mediumMarkRange}
                          </span>
                          {selectedQuestion && (
                            <svg 
                              style={{ 
                                width: '16px', 
                                height: '16px', 
                                color: '#d97706',
                                transform: openDropdown === 'medium' ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s'
                              }} 
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          )}
                        </div>
                      </button>

                      {openDropdown === 'medium' && selectedQuestion && (
                        <div style={{
                          position: 'absolute !important' as any,
                          top: '100% !important' as any,
                          left: '0 !important' as any,
                          right: '0 !important' as any,
                          marginTop: '4px !important' as any,
                          backgroundColor: 'white !important' as any,
                          border: '2px solid #fde68a !important' as any,
                          borderRadius: '8px !important' as any,
                          boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15) !important' as any,
                          zIndex: '10000002 !important' as any,
                          padding: '16px !important' as any,
                          maxHeight: '200px !important' as any,
                          overflowY: 'auto !important' as any
                        }}>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: '#d97706', marginBottom: '8px' }}>
                            Medium Range Rubric ({selectedQuestionData.mediumMarkRange} marks)
                          </div>
                          <div style={{ fontSize: '13px', color: '#374151', lineHeight: '1.5', whiteSpace: 'pre-line' }}>
                            {rubricSections.mediumRange || 'No rubric available for this range.'}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div style={{ position: 'relative' }} data-dropdown-container>
                    <button
                      type="button"
                      onClick={() => {
                        console.log('High Range button clicked, current dropdown:', openDropdown);
                        setOpenDropdown(openDropdown === 'high' ? null : 'high');
                      }}
                      style={{ 
                        width: '100%',
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between', 
                        padding: '12px', 
                        backgroundColor: selectedQuestion ? '#f0fdf4' : '#f9fafb', 
                        borderRadius: '8px', 
                        border: selectedQuestion ? '2px solid #bbf7d0' : '1px solid #e5e7eb',
                        cursor: selectedQuestion ? 'pointer' : 'not-allowed',
                        opacity: selectedQuestion ? 1 : 0.5,
                        transition: 'all 0.2s',
                        boxShadow: selectedQuestion ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none'
                      }}
                      disabled={!selectedQuestion}
                      onMouseEnter={(e) => {
                        if (selectedQuestion) {
                          e.currentTarget.style.backgroundColor = '#ecfdf5';
                          e.currentTarget.style.borderColor = '#86efac';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedQuestion) {
                          e.currentTarget.style.backgroundColor = '#f0fdf4';
                          e.currentTarget.style.borderColor = '#bbf7d0';
                        }
                      }}
                    >
                      <span style={{ fontSize: '14px', fontWeight: '600', color: selectedQuestion ? '#059669' : '#6b7280' }}>
                        High Range {selectedQuestion ? '(Click to view rubric)' : ''}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ 
                          padding: '4px 8px', 
                          backgroundColor: selectedQuestion ? '#059669' : '#9ca3af', 
                          color: 'white', 
                          fontSize: '12px', 
                          fontWeight: '500', 
                          borderRadius: '4px' 
                        }}>
                          {selectedQuestionData.highMarkRange}
                        </span>
                        {selectedQuestion && (
                          <svg 
                            style={{ 
                              width: '16px', 
                              height: '16px', 
                              color: '#059669',
                              transform: openDropdown === 'high' ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s'
                            }} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </div>
                    </button>

                    {openDropdown === 'high' && selectedQuestion && (
                      <div style={{
                        position: 'absolute !important' as any,
                        top: '100% !important' as any,
                        left: '0 !important' as any,
                        right: '0 !important' as any,
                        marginTop: '4px !important' as any,
                        backgroundColor: 'white !important' as any,
                        border: '2px solid #bbf7d0 !important' as any,
                        borderRadius: '8px !important' as any,
                        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15) !important' as any,
                        zIndex: '10000002 !important' as any,
                        padding: '16px !important' as any,
                        maxHeight: '200px !important' as any,
                        overflowY: 'auto !important' as any
                      }}>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#059669', marginBottom: '8px' }}>
                          High Range Rubric ({selectedQuestionData.highMarkRange} marks)
                        </div>
                        <div style={{ fontSize: '13px', color: '#374151', lineHeight: '1.5', whiteSpace: 'pre-line' }}>
                          {rubricSections.highRange || 'No rubric available for this question.'}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Class Questions Carousel */}
          <div style={{ 
            padding: '24px', 
            backgroundColor: 'white', 
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)', 
            border: '1px solid #e5e7eb', 
            borderRadius: '12px' 
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px' }}>
              <svg style={{ width: '20px', height: '20px', color: '#059669' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: 0 }}>Class Questions</h2>
            </div>
            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px' }}>
              Browse all questions in this class ({questions.length} total)
            </p>

            {questions.length > 0 ? (
              <div style={{ position: 'relative' }}>
                {/* Navigation Arrows */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <button
                    type="button"
                    onClick={prevQuestion}
                    disabled={questions.length <= 1}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      border: '1px solid #d1d5db',
                      backgroundColor: 'white',
                      cursor: questions.length <= 1 ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: questions.length <= 1 ? 0.5 : 1,
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (questions.length > 1) {
                        e.currentTarget.style.backgroundColor = '#f9fafb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (questions.length > 1) {
                        e.currentTarget.style.backgroundColor = 'white';
                      }
                    }}
                  >
                    <svg style={{ width: '16px', height: '16px', color: '#6b7280' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>

                  <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>
                    {currentQuestionIndex + 1} of {questions.length}
                  </span>

                  <button
                    type="button"
                    onClick={nextQuestion}
                    disabled={questions.length <= 1}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      border: '1px solid #d1d5db',
                      backgroundColor: 'white',
                      cursor: questions.length <= 1 ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: questions.length <= 1 ? 0.5 : 1,
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (questions.length > 1) {
                        e.currentTarget.style.backgroundColor = '#f9fafb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (questions.length > 1) {
                        e.currentTarget.style.backgroundColor = 'white';
                      }
                    }}
                  >
                    <svg style={{ width: '16px', height: '16px', color: '#6b7280' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                {/* Current Question Display */}
                {(() => {
                  const currentQuestion = getCurrentQuestion();
                  if (!currentQuestion) return null;
                  
                  return (
                    <div style={{ 
                      border: '1px solid #e5e7eb', 
                      borderRadius: '8px', 
                      padding: '16px',
                      backgroundColor: '#f9fafb'
                    }}>
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                          <p style={{ fontSize: '14px', color: '#111827', margin: 0, fontWeight: '500', flex: 1, paddingRight: '16px' }}>
                            {currentQuestion.question}
                          </p>
                          <span style={{ 
                            fontSize: '14px', 
                            fontWeight: '600', 
                            color: '#2563eb',
                            backgroundColor: '#dbeafe',
                            padding: '4px 8px',
                            borderRadius: '6px'
                          }}>
                            {currentQuestion.marks} marks
                          </span>
                        </div>
                        
                        <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                          <span style={{ 
                            padding: '4px 8px', 
                            backgroundColor: '#dc2626', 
                            color: 'white', 
                            fontSize: '12px', 
                            fontWeight: '500', 
                            borderRadius: '4px' 
                          }}>
                            Low: {currentQuestion.lowMarkRange}
                          </span>
                          {currentQuestion.mediumMarkRange && (
                            <span style={{ 
                              padding: '4px 8px', 
                              backgroundColor: '#d97706', 
                              color: 'white', 
                              fontSize: '12px', 
                              fontWeight: '500', 
                              borderRadius: '4px' 
                            }}>
                              Medium: {currentQuestion.mediumMarkRange}
                            </span>
                          )}
                          <span style={{ 
                            padding: '4px 8px', 
                            backgroundColor: '#059669', 
                            color: 'white', 
                            fontSize: '12px', 
                            fontWeight: '500', 
                            borderRadius: '4px' 
                          }}>
                            High: {currentQuestion.highMarkRange}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Dots Indicator */}
                {questions.length > 1 && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    gap: '8px', 
                    marginTop: '16px' 
                  }}>
                    {questions.map((_, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => goToQuestion(index)}
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          border: 'none',
                          backgroundColor: index === currentQuestionIndex ? '#2563eb' : '#d1d5db',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (index !== currentQuestionIndex) {
                            e.currentTarget.style.backgroundColor = '#9ca3af';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (index !== currentQuestionIndex) {
                            e.currentTarget.style.backgroundColor = '#d1d5db';
                          }
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ 
                textAlign: 'center', 
                padding: '32px', 
                color: '#6b7280' 
              }}>
                <p style={{ margin: 0, fontSize: '14px' }}>No questions available. Select a class first.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fullscreen Image Modal */}
      {zoomImg && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            cursor: 'pointer'
          }}
          onClick={() => setZoomImg(null)}
        >
          <button
            onClick={() => setZoomImg(null)}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              width: '40px',
              height: '40px',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '50%',
              color: 'white',
              fontSize: '20px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
          >
            ✕
          </button>
          <img 
            src={zoomImg} 
            alt="Fullscreen view"
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              objectFit: 'contain',
              borderRadius: '8px'
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
    </>
  );
};

export default UploadStudentWork; 